import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { FilterField } from '@/services/tables/filters/fields/filterField'
import { isBoolFilterOperation } from '@/services/types'
import type { ParsedFilterType } from '@/services/tables/types'
import type { FilterStrategyGetPopulateDataResult } from '@/services/tables/filters/strategies/types/filterStrategyGetPopulateDataResult'
import type { BaseFilterStrategy } from '@/services/tables/filters/strategies/baseFilterStrategy'
import type { SelectOption } from '@/services/forms/types'
import { RangeFilterStrategy } from '@/services/tables/filters/strategies/rangeFilterStrategy'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'

@injectable()
export class RangeWithBoolFilterStrategy extends RangeFilterStrategy {
  constructor(@inject(SERVICE_TYPES.BaseFilterStrategy) protected readonly baseFilterStrategy: BaseFilterStrategy) {
    super(baseFilterStrategy)
  }

  public getPopulateData(
    filter: FilterField,
    payload: ParsedFilterType[],
    selectedItems?: SelectOption[],
  ): FilterStrategyGetPopulateDataResult {
    return super.getPopulateData(filter, this.normalizeBoolPayload(filter, payload), selectedItems)
  }

  /**
   * Normalizes boolean values from API responses (e.g. `isNull: true` / `isNull: false`)
   * into string values (`'1'` / `'0'`) that the range filter system expects.
   */
  private normalizeBoolPayload(filter: FilterField, payload: ParsedFilterType[]): ParsedFilterType[] {
    return payload.map((item) => {
      if (item.name !== filter.getName() || !isBoolFilterOperation(item.operation)) {
        return item
      }

      const normalizedValue = coersceBoolean(item.value, '1', '0')

      if (normalizedValue === item.value) {
        return item
      }

      return { ...item, value: normalizedValue }
    })
  }
}
